import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, map, of, switchMap, withLatestFrom } from 'rxjs';
import { McpAccessService } from '@niko-crm/ui';
import { extractError } from '../helpers/error-reader';
import { McpAccessActions } from './mcp-access.actions';
import { selectMcpAccessQuery } from './mcp-access.selectors';

/**
 * Admin side of "Acceso MCP" (BR-MCP-01..09). Every mutation success
 * reloads the current page rather than patching an entity locally - `sync`
 * and `provisioningStatus` are derived server-side, so the client never
 * guesses them (design §4.2, mirrors `ImpersonationSessionEffects`).
 */
@Injectable()
export class McpAccessEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly mcpAccessService = inject(McpAccessService);

  loadGrants$ = createEffect(() =>
    this.actions$.pipe(
      ofType(McpAccessActions.loadGrants),
      switchMap(({ query }) =>
        this.mcpAccessService.list(query).pipe(
          map((result) => McpAccessActions.loadGrantsSuccess({ result })),
          catchError((err) => of(McpAccessActions.loadGrantsFailure({ error: extractError(err) }))),
        ),
      ),
    ),
  );

  createGrant$ = createEffect(() =>
    this.actions$.pipe(
      ofType(McpAccessActions.createGrant),
      switchMap(({ payload }) =>
        this.mcpAccessService.create(payload).pipe(
          map((grant) => McpAccessActions.createGrantSuccess({ grant })),
          catchError((err) => of(McpAccessActions.createGrantFailure({ error: extractError(err) }))),
        ),
      ),
    ),
  );

  updateTenants$ = createEffect(() =>
    this.actions$.pipe(
      ofType(McpAccessActions.updateTenants),
      switchMap(({ id, payload }) =>
        this.mcpAccessService.updateTenants(id, payload).pipe(
          map((grant) => McpAccessActions.updateTenantsSuccess({ grant })),
          catchError((err) =>
            of(McpAccessActions.updateTenantsFailure({ error: extractError(err) })),
          ),
        ),
      ),
    ),
  );

  revokeGrant$ = createEffect(() =>
    this.actions$.pipe(
      ofType(McpAccessActions.revokeGrant),
      switchMap(({ id, payload }) =>
        this.mcpAccessService.revoke(id, payload).pipe(
          map((grant) => McpAccessActions.revokeGrantSuccess({ grant })),
          catchError((err) => of(McpAccessActions.revokeGrantFailure({ error: extractError(err) }))),
        ),
      ),
    ),
  );

  reactivateGrant$ = createEffect(() =>
    this.actions$.pipe(
      ofType(McpAccessActions.reactivateGrant),
      switchMap(({ id }) =>
        this.mcpAccessService.reactivate(id).pipe(
          map((grant) => McpAccessActions.reactivateGrantSuccess({ grant })),
          catchError((err) =>
            of(McpAccessActions.reactivateGrantFailure({ error: extractError(err) })),
          ),
        ),
      ),
    ),
  );

  resendInvitation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(McpAccessActions.resendInvitation),
      switchMap(({ id }) =>
        this.mcpAccessService.resendInvitation(id).pipe(
          map((grant) => McpAccessActions.resendInvitationSuccess({ grant })),
          catchError((err) =>
            of(McpAccessActions.resendInvitationFailure({ error: extractError(err) })),
          ),
        ),
      ),
    ),
  );

  resyncGrant$ = createEffect(() =>
    this.actions$.pipe(
      ofType(McpAccessActions.resyncGrant),
      switchMap(({ id }) =>
        this.mcpAccessService.resync(id).pipe(
          map((grant) => McpAccessActions.resyncGrantSuccess({ grant })),
          catchError((err) => of(McpAccessActions.resyncGrantFailure({ error: extractError(err) }))),
        ),
      ),
    ),
  );

  getInvitationLink$ = createEffect(() =>
    this.actions$.pipe(
      ofType(McpAccessActions.getInvitationLink),
      switchMap(({ id }) =>
        this.mcpAccessService.getInvitationLink(id).pipe(
          map((invitation) => McpAccessActions.getInvitationLinkSuccess({ id, invitation })),
          catchError((err) =>
            of(McpAccessActions.getInvitationLinkFailure({ error: extractError(err) })),
          ),
        ),
      ),
    ),
  );

  /** After ANY mutation the current page is fetched again with the same filters. */
  reloadAfterMutation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        McpAccessActions.createGrantSuccess,
        McpAccessActions.updateTenantsSuccess,
        McpAccessActions.revokeGrantSuccess,
        McpAccessActions.reactivateGrantSuccess,
        McpAccessActions.resendInvitationSuccess,
        McpAccessActions.resyncGrantSuccess,
      ),
      withLatestFrom(this.store.select(selectMcpAccessQuery)),
      map(([, query]) => McpAccessActions.loadGrants({ query })),
    ),
  );
}
